import { useState } from "react";
import Seo from "@/components/Seo";
import { fetchAdminAppointments, fetchAdminEnquiries } from "@/lib/api";
import { Lock } from "lucide-react";

const Admin = () => {
    const [key, setKey] = useState(sessionStorage.getItem("bsk_admin_key") || "");
    const [authed, setAuthed] = useState(false);
    const [tab, setTab] = useState("appointments");
    const [appointments, setAppointments] = useState([]);
    const [enquiries, setEnquiries] = useState([]);
    const [error, setError] = useState("");

    const load = async (k) => {
        try {
            const [a, e] = await Promise.all([fetchAdminAppointments(k), fetchAdminEnquiries(k)]);
            setAppointments(a.items);
            setEnquiries(e.items);
            setAuthed(true);
            sessionStorage.setItem("bsk_admin_key", k);
        } catch {
            setError("Incorrect access key.");
        }
    };

    if (!authed)
        return (
            <main className="min-h-screen flex items-center justify-center px-5">
                <Seo title="Admin | BSK Jewellers" siteName="BSK Jewellers" />
                <form
                    onSubmit={(e) => { e.preventDefault(); setError(""); load(key); }}
                    className="w-full max-w-sm border border-gold/25 bg-charcoal/50 p-8 text-center"
                    data-testid="admin-login-form"
                >
                    <Lock size={22} strokeWidth={1.25} className="text-gold mx-auto" />
                    <h1 className="font-serif text-2xl mt-4">BSK Admin</h1>
                    <input
                        type="password"
                        value={key}
                        onChange={(e) => setKey(e.target.value)}
                        data-testid="admin-key-input"
                        placeholder="Access key"
                        className="w-full mt-6 bg-obsidian/60 border border-gold/20 focus:border-gold outline-none px-4 py-3 text-sm text-ivory placeholder:text-sand/50"
                    />
                    {error && <p className="text-red-400 text-xs mt-3" data-testid="admin-error">{error}</p>}
                    <button type="submit" data-testid="admin-login-btn" className="w-full mt-5 bg-gold text-obsidian text-[11px] tracking-[0.25em] uppercase py-3.5 hover:bg-gold-bright transition-colors">
                        Enter
                    </button>
                </form>
            </main>
        );

    return (
        <main className="pt-32 pb-24 min-h-screen px-5 sm:px-8 max-w-5xl mx-auto">
            <Seo title="Admin | BSK Jewellers" siteName="BSK Jewellers" />
            <h1 className="font-serif text-4xl" data-testid="admin-heading">Enquiries &amp; Appointments</h1>
            <div className="flex gap-3 mt-8">
                <button onClick={() => setTab("appointments")} data-testid="admin-tab-appointments" className={`text-[11px] tracking-[0.2em] uppercase px-5 py-2.5 border ${tab === "appointments" ? "bg-gold text-obsidian border-gold" : "border-gold/30 text-sand"}`}>
                    Appointments ({appointments.length})
                </button>
                <button onClick={() => setTab("enquiries")} data-testid="admin-tab-enquiries" className={`text-[11px] tracking-[0.2em] uppercase px-5 py-2.5 border ${tab === "enquiries" ? "bg-gold text-obsidian border-gold" : "border-gold/30 text-sand"}`}>
                    Messages ({enquiries.length})
                </button>
            </div>
            <div className="mt-8 space-y-4">
                {tab === "appointments" &&
                    (appointments.length ? (
                        appointments.map((a) => (
                            <div key={a.id} className="border border-gold/15 bg-charcoal/40 p-5 text-sm" data-testid={`admin-appointment-${a.id}`}>
                                <div className="flex flex-wrap justify-between gap-2">
                                    <p className="font-serif text-lg text-ivory">{a.name} <span className="text-gold text-xs ml-2">{a.interest || "General"}</span></p>
                                    <p className="text-sand text-xs">{a.date || "—"} {a.time || ""}</p>
                                </div>
                                <p className="text-sand mt-2">{a.phone} {a.email ? `· ${a.email}` : ""}</p>
                                {a.message && <p className="text-sand/80 mt-2 italic">{a.message}</p>}
                            </div>
                        ))
                    ) : (
                        <p className="text-sand italic font-serif text-xl" data-testid="admin-no-appointments">No appointment requests yet.</p>
                    ))}
                {tab === "enquiries" &&
                    (enquiries.length ? (
                        enquiries.map((e) => (
                            <div key={e.id} className="border border-gold/15 bg-charcoal/40 p-5 text-sm" data-testid={`admin-enquiry-${e.id}`}>
                                <p className="font-serif text-lg text-ivory">{e.name}</p>
                                <p className="text-sand mt-1">{e.phone || ""} {e.email ? `· ${e.email}` : ""}</p>
                                <p className="text-sand/80 mt-2 italic">{e.message}</p>
                            </div>
                        ))
                    ) : (
                        <p className="text-sand italic font-serif text-xl" data-testid="admin-no-enquiries">No messages yet.</p>
                    ))}
            </div>
        </main>
    );
};

export default Admin;
